/**
 * PreviewCard — a Harper line run over the last seven days, posting nothing.
 *
 * The page starts a preview (of the saved line, or of unsaved changes) and
 * this card follows it: live progress while it runs, then the Update it would
 * post, any setting the instructions disagreed with, and the same collapsed
 * "How this was written" the case file shows. The latest preview is kept on
 * the line, so a refresh still shows it; it says when it was made, and when
 * the instructions or Approach changed since.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  APPROACH_KEYS,
  normalizeApproach,
  type HarperApproach,
  type HarperPreview,
  type HarperStageName,
  type WrittenAs,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { HonestyNotice } from "../ui/index.js";
import { HarperUpdateView, HowItWasWritten, degradedSentences } from "../work/harperUpdate.js";
import { LINE_ADD_CAPACITY, LINE_ADD_CAPACITY_HREF } from "./copy.js";
import { TeamWriteError, fetchPreview, startPreview } from "./teamsApi.js";

/** How often a running preview is re-read. */
export const PREVIEW_POLL_MS = 3000;

/** The inputs a preview runs under: the instructions, the Approach and the leave-out rule. */
export interface PreviewInputs {
  instructions: string | null;
  approach: HarperApproach;
  /** Absent reads as none (a preview stored before the blank existed). */
  notRelevant?: string | null;
  /** The line's Written as; absent on a preview stored before the field. */
  category?: WrittenAs;
}

const STEP_ACTIVE: Record<HarperStageName, string> = {
  gather: "Collecting",
  dossiers: "Reading",
  editor: "Editing",
  writer: "Writing",
  critic: "Checking",
  deliver: "Posting",
};

function clean(value: string | null | undefined): string | null {
  return value && value.trim() ? value.trim() : null;
}

/** Whether two sets of inputs would preview the same thing. */
export function sameInputs(a: PreviewInputs, b: PreviewInputs): boolean {
  const left = normalizeApproach(a.approach);
  const right = normalizeApproach(b.approach);
  return (
    clean(a.instructions) === clean(b.instructions) &&
    clean(a.notRelevant) === clean(b.notRelevant) &&
    // A preview from before the field ran under whatever was saved.
    (a.category === undefined || b.category === undefined || a.category === b.category) &&
    APPROACH_KEYS.every((key) => left[key] === right[key])
  );
}

/** "Reading 58 changes · step 2 of 5" */
export function progressLine(progress: NonNullable<HarperPreview["progress"]>): string {
  const what =
    progress.candidates !== null && progress.stage !== "gather"
      ? `${STEP_ACTIVE[progress.stage]} ${progress.candidates} ${progress.candidates === 1 ? "change" : "changes"}`
      : STEP_ACTIVE[progress.stage];
  return `${what} · step ${progress.step} of ${progress.of}`;
}

/** "Previewed 12 min ago" */
export function previewedAgo(at: string, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.round((now - new Date(at).getTime()) / 60_000));
  if (minutes < 1) return "Previewed just now";
  if (minutes < 60) return `Previewed ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Previewed ${hours} h ago`;
  return `Previewed ${new Date(at).toLocaleDateString()}`;
}

export interface PreviewCardProps {
  deskId: string;
  jobId: string;
  /** What the page shows now, saved or not. */
  current: PreviewInputs;
  /** What is saved on the line. */
  saved: PreviewInputs;
  canPreview: boolean;
  /** Bumped by the page's Preview buttons: start a preview of `current`. */
  startNonce: number;
  /** The line has never run: offer a preview before the first post. */
  neverRan: boolean;
  onSave: () => void;
  onStart: () => void;
}

export function PreviewCard({
  deskId,
  jobId,
  current,
  saved,
  canPreview,
  startNonce,
  neverRan,
  onSave,
  onStart,
}: PreviewCardProps) {
  const [preview, setPreview] = useState<HarperPreview | null>(null);
  const [message, setMessage] = useState<{ text: string; addCapacity: boolean } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [starting, setStarting] = useState(false);

  const read = useCallback(async () => {
    try {
      setPreview(await fetchPreview(deskId, jobId));
    } catch (err) {
      captureUiError(err, { area: "Harper", operation: "harper_preview_read", severity: "warning", expected: true });
    }
  }, [deskId, jobId]);

  useEffect(() => {
    void read();
  }, [read]);

  const running = preview?.status === "running";
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void read(), PREVIEW_POLL_MS);
    return () => clearInterval(timer);
  }, [running, read]);

  const lastNonce = useRef(startNonce);
  const currentRef = useRef(current);
  currentRef.current = current;
  const savedRef = useRef(saved);
  savedRef.current = saved;
  useEffect(() => {
    if (startNonce === lastNonce.current) return;
    lastNonce.current = startNonce;
    const inputs = currentRef.current;
    const draft = sameInputs(inputs, savedRef.current)
      ? null
      : {
          instructions: clean(inputs.instructions),
          approach: normalizeApproach(inputs.approach),
          notRelevant: clean(inputs.notRelevant),
          ...(inputs.category ? { category: inputs.category } : {}),
        };
    setStarting(true);
    setMessage(null);
    setExpanded(false);
    void startPreview(deskId, jobId, draft)
      .then(setPreview)
      .catch((err: unknown) => {
        if (err instanceof TeamWriteError) {
          setMessage({ text: err.message, addCapacity: err.code === "no_capacity" });
          return;
        }
        captureUiError(err, { area: "Harper", operation: "harper_preview_start", severity: "warning", expected: true });
        setMessage({ text: "Could not start the preview just now.", addCapacity: false });
      })
      .finally(() => setStarting(false));
  }, [startNonce, deskId, jobId]);

  if (!preview && !message && !starting) {
    return neverRan && canPreview ? (
      <p className="ln-preview-callout" data-testid="line-preview-callout">
        See what this would post.{" "}
        <button type="button" className="tm-link-btn" data-testid="line-preview-callout-start" onClick={onStart}>
          Preview
        </button>
      </p>
    ) : null;
  }

  const stale = preview && preview.status !== "running" && preview.inputs ? !sameInputs(preview.inputs, current) : false;
  const offerSave =
    preview?.status === "done" && preview.draft && !stale && !sameInputs(current, saved) && canPreview;
  const fellBack = preview?.report?.quality.safeMode ? degradedSentences(preview.report) : [];

  return (
    <section className="ln-section ln-preview" data-testid="line-preview">
      <div className="ln-preview-head">
        <span className="tm-field-label">Preview · last 7 days</span>
        {preview && preview.status !== "running" && (
          <span className="ln-preview-when" data-testid="line-preview-when">
            {previewedAgo(preview.finishedAt ?? preview.startedAt)}
            {preview.draft ? " · with unsaved changes" : ""}
          </span>
        )}
        <span className="tm-head-spacer" />
        {preview?.status === "done" && preview.update && (
          <button
            type="button"
            className="tm-quiet-btn"
            data-testid="line-preview-expand"
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
        {canPreview && !running && (
          <button
            type="button"
            className="tm-quiet-btn"
            data-testid="line-preview-again"
            disabled={starting}
            onClick={onStart}
          >
            {starting ? "Starting…" : "Preview again"}
          </button>
        )}
      </div>

      {message && (
        <p className="tm-message" role="status" data-testid="line-preview-message">
          {message.text}{" "}
          {message.addCapacity && <Link to={LINE_ADD_CAPACITY_HREF}>{LINE_ADD_CAPACITY}</Link>}
        </p>
      )}

      {stale && (
        <p className="ln-preview-stale" data-testid="line-preview-stale">
          Changed since this preview.{" "}
          {canPreview && (
            <button type="button" className="tm-link-btn" onClick={onStart}>
              Preview again
            </button>
          )}
        </p>
      )}

      {(running || starting) && (
        <p className="ln-preview-progress" role="status" data-testid="line-preview-progress">
          {preview?.progress && running ? progressLine(preview.progress) : "Starting the preview…"}
        </p>
      )}

      {preview && preview.status === "done" && !preview.update && (
        <p className="ln-note-text" data-testid="line-preview-quiet">
          Nothing in the last seven days met this job's bar, so it would post nothing.
        </p>
      )}
      {preview && (preview.status === "failed" || preview.status === "held" || preview.status === "interrupted") && (
        <p className="tm-message" data-testid="line-preview-stopped">
          {preview.status === "interrupted"
            ? "The preview was interrupted by a restart. Preview again."
            : (preview.error ?? "The preview stopped.")}{" "}
          {preview.status === "held" && <Link to={LINE_ADD_CAPACITY_HREF}>{LINE_ADD_CAPACITY}</Link>}
        </p>
      )}

      {preview?.status === "done" && preview.update && (
        <>
          {fellBack.length > 0 && (
            <HonestyNotice>
              <span data-testid="line-preview-safe-mode">{fellBack.join(" ")}</span>
            </HonestyNotice>
          )}
          {preview.report?.approach.conflicts.map((conflict) => (
            <p key={conflict} className="hrun-conflict" data-testid="line-preview-conflict">
              {conflict}
            </p>
          ))}
          <div
            className={"ln-preview-body" + (expanded ? " is-expanded" : "")}
            data-testid="line-preview-body"
          >
            <HarperUpdateView update={preview.update} />
          </div>
          {preview.report && <HowItWasWritten report={preview.report} />}
          <p className="ln-note-text">Nothing was posted.</p>
          {offerSave && (
            <button type="button" className="tm-primary-btn" data-testid="line-preview-save" onClick={onSave}>
              Save these changes
            </button>
          )}
        </>
      )}
    </section>
  );
}
